import React, { useState } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type {
	JiraVersionUpdatedWarningConfigInput,
	ui_ManageWarningsModalVersionWarningConfigMutation$data as MutationResponse,
} from '@atlassian/jira-relay/src/__generated__/ui_ManageWarningsModalVersionWarningConfigMutation.graphql';
import type { ui_softwareReleasesManageWarnings_ManageWarningsModal$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesManageWarnings_ManageWarningsModal.graphql';
import messages from './messages.tsx';
import { Toggle } from './toggle/index.tsx';
import {
	convertStatusToBoolean,
	getNewWarningConfigValues,
	useRemoveDisabledWarningFilters,
} from './utils.tsx';

export const ManageWarningsModal = ({
	setModalClosed,
	jiraVersion,
}: {
	jiraVersion: ui_softwareReleasesManageWarnings_ManageWarningsModal$key;
	setModalClosed: () => void;
}): React.JSX.Element => {
	const warningsSetForAllVersionsInThisProjectGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.warningsSetForAllVersionsInThisProjectGalaxia
		: messages.warningsSetForAllVersionsInThisProject;
	const { formatMessage } = useIntl();

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment ui_softwareReleasesManageWarnings_ManageWarningsModal on JiraVersion {
				project @required(action: THROW) {
					id @required(action: THROW)
				}
				__id
				id
				warningConfig @required(action: THROW) {
					openPullRequest @required(action: THROW)
					openReview @required(action: THROW)
					unreviewedCode @required(action: THROW)
					failingBuild @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const { warningConfig, __id: versionRecordId } = version;
	const { openPullRequest, failingBuild, unreviewedCode, openReview } = warningConfig;
	const [updatedVersionWarningConfig, setUpdatedVersionWarningConfig] =
		useState<JiraVersionUpdatedWarningConfigInput>(convertStatusToBoolean(warningConfig));
	const removeDisabledWarningFilters = useRemoveDisabledWarningFilters(updatedVersionWarningConfig);
	const [isSaveFailed, setIsSaveFailed] = useState<boolean>(false);
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateVersionWarningConfig, isWarningConfigUpdating] = useMutation(graphql`
		mutation ui_ManageWarningsModalVersionWarningConfigMutation(
			$input: JiraUpdateVersionWarningConfigInput!
			$versionId: ID!
		) {
			jira {
				updateVersionWarningConfig(input: $input) {
					success
					version(id: $versionId) {
						... on JiraVersion {
							...issueProgressDetails_versionProgress
						}
					}
				}
			}
		}
	`);
	const jiraProjectId = version.project.id;

	return (
		<ShortcutScope>
			<ModalDialog
				messageId="software-releases-manage-warnings.ui.modal-dialog"
				messageType="transactional"
				testId="software-releases-manage-warnings.ui.modal-dialog"
			>
				<ModalHeader hasCloseButton={fg('enghealth-41206-modal-header-close-button')}>
					<ModalTitle>{formatMessage(messages.manageWarnings)}</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<WarningDescription>
						{formatMessage(
							fg('jira-issue-terminology-update-m4')
								? messages.warningDescriptionIssueTerminologyRefreshM4
								: messages.warningDescription,
						)}
					</WarningDescription>
					{isSaveFailed && (
						<Box xcss={errorSectionMessageContainerStyles}>
							<SectionMessage appearance="error">
								{formatMessage(messages.updateWarningConfigFailed)}
							</SectionMessage>
						</Box>
					)}
					<SectionMessage appearance="warning">
						{formatMessage(warningsSetForAllVersionsInThisProjectGalaxia)}
					</SectionMessage>
					{openPullRequest !== 'NOT_APPLICABLE' && (
						<Toggle
							testId="software-releases-manage-warnings.ui.open-pull-request"
							isDisabled={isWarningConfigUpdating}
							initialValue={openPullRequest === 'ENABLED'}
							onChange={(_, analyticsEvent: UIAnalyticsEvent) =>
								setUpdatedVersionWarningConfig((prev) => {
									fireUIAnalytics(analyticsEvent, 'versionWarningsConfig', {
										warningType: 'openPullRequest',
										isEnabled: !prev.isOpenPullRequestEnabled,
									});
									return {
										...prev,
										isOpenPullRequestEnabled: !prev.isOpenPullRequestEnabled,
									};
								})
							}
							headingTranslated={formatMessage(messages.openPullRequests)}
							descriptionTranslated={formatMessage(
								fg('jira-issue-terminology-update-m4')
									? messages.openPullRequestsDescriptionIssueTerminologyRefreshM4
									: messages.openPullRequestsDescription,
							)}
						/>
					)}
					{openReview !== 'NOT_APPLICABLE' && (
						<Toggle
							testId="software-releases-manage-warnings.ui.open-review"
							isDisabled={isWarningConfigUpdating}
							initialValue={openReview === 'ENABLED'}
							onChange={(_, analyticsEvent: UIAnalyticsEvent) =>
								setUpdatedVersionWarningConfig((prev) => {
									fireUIAnalytics(analyticsEvent, 'versionWarningsConfig', {
										warningType: 'openReview',
										isEnabled: !prev.isOpenReviewEnabled,
									});
									return {
										...prev,
										isOpenReviewEnabled: !prev.isOpenReviewEnabled,
									};
								})
							}
							headingTranslated={formatMessage(messages.openReviews)}
							descriptionTranslated={formatMessage(
								fg('jira-issue-terminology-update-m4')
									? messages.openReviewsDescriptionIssueTerminologyRefreshM4
									: messages.openReviewsDescription,
							)}
						/>
					)}
					{unreviewedCode !== 'NOT_APPLICABLE' && (
						<Toggle
							testId="software-releases-manage-warnings.ui.unreviewed-code"
							isDisabled={isWarningConfigUpdating}
							initialValue={unreviewedCode === 'ENABLED'}
							onChange={(_, analyticsEvent: UIAnalyticsEvent) => {
								setUpdatedVersionWarningConfig((prev) => {
									fireUIAnalytics(analyticsEvent, 'versionWarningsConfig', {
										warningType: 'unreviewedCode',
										isEnabled: !prev.isUnreviewedCodeEnabled,
									});
									return {
										...prev,
										isUnreviewedCodeEnabled: !prev.isUnreviewedCodeEnabled,
									};
								});
							}}
							headingTranslated={formatMessage(messages.unreviewedCode)}
							descriptionTranslated={formatMessage(
								fg('jira-issue-terminology-update-m4')
									? messages.unreviewedCodeDescriptionIssueTerminologyRefreshM4
									: messages.unreviewedCodeDescription,
							)}
						/>
					)}
					{failingBuild !== 'NOT_APPLICABLE' && (
						<Toggle
							testId="software-releases-manage-warnings.ui.failing-build"
							isDisabled={isWarningConfigUpdating}
							initialValue={failingBuild === 'ENABLED'}
							onChange={(_, analyticsEvent: UIAnalyticsEvent) => {
								setUpdatedVersionWarningConfig((prev) => {
									fireUIAnalytics(analyticsEvent, 'versionWarningsConfig', {
										warningType: 'failingBuild',
										isEnabled: !prev.isFailingBuildEnabled,
									});
									return {
										...prev,
										isFailingBuildEnabled: !prev.isFailingBuildEnabled,
									};
								});
							}}
							headingTranslated={formatMessage(messages.failingBuilds)}
							descriptionTranslated={formatMessage(
								fg('jira-issue-terminology-update-m4')
									? messages.failingBuildsDescriptionIssueTerminologyRefreshM4
									: messages.failingBuildsDescription,
							)}
						/>
					)}
				</ModalBody>
				<ModalFooter>
					<LoadingButton
						appearance="primary"
						onClick={() => {
							updateVersionWarningConfig({
								variables: {
									input: {
										jiraProjectId,
										updatedVersionWarningConfig,
									},
									versionId: version.id,
								},

								// @ts-expect-error - TS2322 - Type '({ jira: { updateVersionWarningConfig: { success }, }, }: MutationResponse) => void' is not assignable to type '(response: {}, errors: PayloadError[] | null) => void | null'.
								onCompleted: ({
									jira: {
										// @ts-expect-error(Suppressed for relay bump) - Property 'updateVersionWarningConfig' does not exist on type '{ readonly updateVersionWarningConfig: { readonly success: boolean; readonly version: { readonly " $fragmentSpreads": FragmentRefs<"issueProgressDetails_versionProgress">; } | null | undefined; } | null | undefined; } | null | undefined'.
										updateVersionWarningConfig: { success },
									},
								}: MutationResponse) => {
									if (success) {
										removeDisabledWarningFilters();
										setModalClosed();
										return;
									}

									setIsSaveFailed(true);
								},
								onError: () => {
									setIsSaveFailed(true);
								},
								updater: (store) => {
									const versionRecord = store.get(versionRecordId);
									if (versionRecord == null) return;
									const existingWarningConfigRecord =
										versionRecord.getLinkedRecord('warningConfig');
									if (existingWarningConfigRecord == null) return;
									const newValues = getNewWarningConfigValues(updatedVersionWarningConfig, {
										openPullRequest,
										openReview,
										failingBuild,
										unreviewedCode,
									});
									existingWarningConfigRecord.setValue(
										newValues.openPullRequest,
										'openPullRequest',
									);
									existingWarningConfigRecord.setValue(newValues.openReview, 'openReview');
									existingWarningConfigRecord.setValue(newValues.unreviewedCode, 'unreviewedCode');
									existingWarningConfigRecord.setValue(newValues.failingBuild, 'failingBuild');

									versionRecord.setLinkedRecord(existingWarningConfigRecord, 'warningConfig');
								},
							});
						}}
						isLoading={isWarningConfigUpdating}
					>
						{formatMessage(messages.modalSave)}
					</LoadingButton>
					<Button
						testId="software-releases-manage-warnings.ui.cancel-manage-warnings"
						onClick={() => setModalClosed()}
						isDisabled={isWarningConfigUpdating}
					>
						{formatMessage(messages.modalCancel)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
};

const errorSectionMessageContainerStyles = xcss({
	marginBottom: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WarningDescription = styled.p({
	marginBottom: token('space.300'),

	color: `${token('color.text')}`,
});
